// Client copy of user-service/src/modules/broadcast/broadcast.renderer.js,
// used only for the live compose preview. The server render is authoritative.

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
const TIMEZONE = "Africa/Cairo";
const LOCALES = { en: "en-GB", ar: "ar-EG" };

function formatDateTime(raw, lang) {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  const dateOnly = typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim());
  const opts = dateOnly ? { dateStyle: "full", timeZone: "UTC" } : { dateStyle: "full", timeStyle: "short", timeZone: TIMEZONE };
  try {
    return new Intl.DateTimeFormat(LOCALES[lang] || LOCALES.en, opts).format(d);
  } catch {
    return d.toISOString();
  }
}

function formatValue(def, raw, lang) {
  if (raw === undefined || raw === null || raw === "") return "";
  if (def?.type === "datetime") return formatDateTime(raw, lang);
  if (def?.type === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? new Intl.NumberFormat(LOCALES[lang] || LOCALES.en).format(n) : String(raw);
  }
  return String(raw);
}

export function extractPlaceholders(...texts) {
  const keys = new Set();
  texts.forEach((t) => {
    if (!t) return;
    for (const m of String(t).matchAll(PLACEHOLDER_RE)) keys.add(m[1]);
  });
  return [...keys];
}

export function renderText(text, variables = [], values = {}, lang = "en") {
  if (!text) return text || "";
  const defs = new Map((variables || []).map((v) => [v.key, v]));
  return String(text).replace(PLACEHOLDER_RE, (whole, key) => {
    const v = values?.[key];
    if (v === undefined || v === null || v === "") return whole;
    return formatValue(defs.get(key), v, lang);
  });
}

export function renderContent({ titleEn, titleAr, bodyEn, bodyAr }, variables, values) {
  return {
    titleEn: renderText(titleEn, variables, values, "en"),
    titleAr: titleAr ? renderText(titleAr, variables, values, "ar") : "",
    bodyEn: renderText(bodyEn, variables, values, "en"),
    bodyAr: bodyAr ? renderText(bodyAr, variables, values, "ar") : "",
  };
}

/** Variables required but empty + placeholders that have no variable definition. */
export function findProblems(content, variables, values, language = "en") {
  const missing = (variables || [])
    .filter((v) => v.required && (values?.[v.key] === undefined || String(values[v.key]).trim() === ""))
    .map((v) => (language === "ar" && v.labelAr) || v.labelEn || v.key);
  const defined = new Set((variables || []).map((v) => v.key));
  const undefinedKeys = extractPlaceholders(content.titleEn, content.titleAr, content.bodyEn, content.bodyAr).filter(
    (k) => !defined.has(k)
  );
  return { missing, undefinedKeys };
}

// <input type="datetime-local"> works in local wall-clock time without a zone.
export const toLocalInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

// Page timestamps (history, confirmations): Arabic month names, Latin digits —
// like the rest of the admin tables. The message content itself keeps ar-EG.
const UI_LOCALES = { en: "en-GB", ar: "ar-EG-u-nu-latn" };
export const formatWhen = (date, language = "en") => {
  if (!date) return "—";
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? "—"
    : new Intl.DateTimeFormat(UI_LOCALES[language] || UI_LOCALES.en, { dateStyle: "medium", timeStyle: "short" }).format(d);
};
